import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { Check, Circle, InfoIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';

import EditOrderDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/dialogs/edit-order-dialog';
import StandardOrderRowBody from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/standard/standard-order-row-body';
import { ActiveOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/order-assortment.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { OrderStatus } from 'views/order-feed/types/order-status.enum';

type Props = {
  order: ActiveOrder;
};

const cellAlignment = {
  width: '14%',
  maxWidth: '14%',
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders.list';

const StandardOrderRow: React.FC<Props> = ({ order }) => {
  const theme = useTheme();
  const confirmDays = order?.remainingEditDays ?? 0;
  const { createOrderFeedEvent } = useGoogleTagManager();
  const arrivalTime = order.arrivalTime ?? order.currentEta ?? null;

  const arrivalDate = useMemo(() => {
    if (order.arrivalTime) return order.arrivalTime; // the order has been delivered
    else if (order.currentEta) return order.currentEta; // the order has been planned on an Anteo shipment
    else return order.estimatedArrivalDate; // date is too far in to the future, so no Anteo shipment has been planned yet.
  }, [order]);

  const changesTextId = useMemo(() => {
    if (confirmDays === 0) return `${PAGE_NAME_ID}.notPossible`; // no changes possible with 0 days left
    if (confirmDays === 1) return `${PAGE_NAME_ID}.today`; // final day to change
    return `${PAGE_NAME_ID}.days`; //
  }, [order]);

  return (
    <NewListRow
      colSpan={8}
      body={<StandardOrderRowBody order={order} />}
      bodyOnClick={() => {
        createOrderFeedEvent('dropdown', {
          orderType: OrderType.CONFIRMED,
          orderNumber: order.orderNumber,
          supplyPlanType: SupplyPlanType.OFD2,
        });
      }}>
      <ListCell sx={cellAlignment}>
        <Box display={'flex'} gap={4} alignItems={'center'}>
          <IconButton sx={{ backgroundColor: theme.palette.green.o50, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
            <Check color={theme.palette.common.black} size={17} />
          </IconButton>
          <Box>#{order.orderNumber}</Box>
        </Box>
      </ListCell>
      <ListCell>
        {!!order.arrivalTime ? (
          <FormattedMessage id={`${PAGE_NAME_ID}.delivered`} />
        ) : (
          <FormattedMessage id={changesTextId} values={{ days: confirmDays }} />
        )}
      </ListCell>
      <ListCell sx={cellAlignment}>{dayjs(order.dispatchDate).format('DD MMM YYYY')}</ListCell>
      <ListCell sx={cellAlignment}>
        <Box display={'flex'} alignItems={'center'} flexWrap={'nowrap'} gap={1}>
          <Box component={'span'} width={'86px'}>
            {arrivalDate !== null ? dayjs(arrivalDate).format('DD MMM YYYY') : '-'}
          </Box>
          {arrivalTime && (
            <Tooltip
              placement="top"
              title={
                <Box display="inline-flex" gap={1}>
                  <Typography fontWeight="bold">
                    <FormattedMessage id={`${PAGE_NAME_ID}.arrivalTime`} /> -
                  </Typography>
                  <Typography>{dayjs(arrivalTime).format('HH:mm')}</Typography>
                </Box>
              }>
              <InfoIcon size={15} strokeWidth={3} style={{ marginBottom: '0.2em' }} />
            </Tooltip>
          )}
        </Box>
      </ListCell>
      <ListCell sx={cellAlignment}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
          {order.orderStatus !== OrderStatus.Confirmed && (
            <Circle size={8} fill={theme.palette.darkBlue.o25} color={theme.palette.darkBlue.o25} />
          )}
          <FormattedNumber
            value={order.totalOrderQuantity}
            style={`unit`}
            unit={'kilogram'}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </Box>
      </ListCell>
      <ListCell align="right" colSpan={0}>
        {order.orderStatus === OrderStatus.Confirmed && (
          <EditOrderDialog order={order} />
        )}
      </ListCell>
    </NewListRow>
  );
};

export default React.memo(StandardOrderRow);
